import React from 'react';
import dayjs from 'dayjs';

import type { NormalizedQuote } from '../../../apis/lanePlanner';
import { formatMoney, travelTextOf } from './quoteView';
import { buildCalendar, CalendarRow } from './resultsView';
import styles from './styles.module.css';

interface CalendarGridProps {
  calendar: ReturnType<typeof buildCalendar>;
  /** Opens a cell's quote in the list view. */
  onOpenQuote: (quote: NormalizedQuote) => void;
}

/** A carrier's lowest price across the days shown, or null when every day costs the same (nothing to single out). */
const rowLow = (row: CalendarRow): number | null => {
  const prices = Object.values(row.cells)
    .map((cell) => Number(cell?.price))
    .filter((price) => Number.isFinite(price));
  const low = Math.min(...prices);
  return prices.length > 1 && prices.some((price) => price > low) ? low : null;
};

/** Carrier x day grid of lowest prices; a cell opens its quote. */
const TransitSub: React.FC<{ quote: NormalizedQuote }> = ({ quote }) => {
  const transit = travelTextOf(quote);
  return transit ? <span className={styles.calendarSub}>{transit}</span> : null;
};

const CalendarGrid: React.FC<CalendarGridProps> = ({
  calendar,
  onOpenQuote,
}) => {
  if (!calendar.rows.length) {
    return (
      <div className={styles.emptyState}>No priced quotes to show by day.</div>
    );
  }
  return (
    <div className={styles.calendarScroll}>
      <table className={styles.calendarTable}>
        <thead>
          <tr>
            <th scope='col'>Carrier</th>
            {calendar.days.map((day) => (
              <th scope='col' key={day}>
                {dayjs(day).format('ddd D')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calendar.rows.map((row) => (
            <tr key={row.carrier}>
              <th scope='row'>{row.carrier}</th>
              {calendar.days.map((day) => {
                const cell = row.cells[day];
                const lowest = cell && Number(cell.price) === rowLow(row);
                return (
                  <td key={day}>
                    {cell ? (
                      <button
                        type='button'
                        className={`${styles.calendarCell} ${
                          lowest ? styles.calendarCellLow : ''
                        }`}
                        aria-label={`${row.carrier} ${dayjs(day).format('ddd D')} ${formatMoney(cell.price, calendar.currency)}`}
                        onClick={() => onOpenQuote(cell.quote)}
                      >
                        {formatMoney(cell.price, calendar.currency).replace(
                          /\.00$/,
                          ''
                        )}
                        <TransitSub quote={cell.quote} />
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.calendarCaption}>
        Lowest rate per departure day. Select a cell to open the option.
      </div>
    </div>
  );
};

export default CalendarGrid;
